//******************************************************************************
/*
	options.jsとstyle.jsは<script>タグで読み込みグルーバルのスコープを持つ
	options.js
		pcpng.tileSize
			MapLibre-GLのデフォルトタイルサイズが512なので512指定で等倍
				表示中地図のズームレベルのタイルが呼ばれる
			タイルサイズを256で指定（2のマイナス1乗倍）すると1つ上のズームレベルのタイルが呼ばれる
				デフォルトサイズの1つのタイルにつき4枚のタイルを呼ぶことになるので
				map.maxZoomは点群PNGタイルの最大ズームレベル-1
			タイルサイズを1024で指定（2の1乗倍）すると1つ下のズームレベルのタイルが呼ばれる
				デフォルトサイズの1つのタイルにつき1/4枚のタイルを呼ぶことになるので
				map.maxZoomは点群PNGタイルの最大ズームレベル+1
*/
//******************************************************************************
// import
//------------------------------------------------------------------------------
// maplibre
import MapLibreGL from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
//------------------------------------------------------------------------------
// deck.gl
import { COORDINATE_SYSTEM } from "@deck.gl/core";
import { MapboxOverlay } from "@deck.gl/mapbox";
//------------------------------------------------------------------------------
// deck.glのPointCloudLayerを拡張してフラグメントシェーダのif文を削除したレイヤクラスは削除
// deck.glのPointCloudLayerを拡張して標高のグラデーション表示を可能にするためのクラスを作成
import { CustomPointCloudLayer } from "./modules/CustomPointCloudLayer/CustomPointCloudLayer";
//------------------------------------------------------------------------------
// 計測用の線分と球の描画用
import { MeasureSegments } from "./modules/Measure/MeasureSegments";
import { MeasureSpheres } from "./modules/Measure/MeasureSpheres";
// ポップアップ用
import { TriggerMeasure } from "./modules/Measure/TriggerMeasure";
//------------------------------------------------------------------------------
// オンマウスの点群をハイライト
import { OnMousePoint } from "./modules/OnMousePoint/OnMousePoint";
//------------------------------------------------------------------------------
// 点群クリックの頂点情報を取得する関数（分離した）
import { getPickedVertex } from "./modules/getPickingVertex/getPickingVertex";
//------------------------------------------------------------------------------
// タイルキーに関するユーティリティ
import { keyToTileCoords, tileCoordsToKey, keyInBounds } from "./modules/keyUtils/keyUtils";
//------------------------------------------------------------------------------
// 先祖、子孫のタイルを取得する関数
import { findChildren, findParents } from "./modules/findFamily/findFamily";
//------------------------------------------------------------------------------
// lodash（throttleを使う）
import Lodash from "lodash";
//------------------------------------------------------------------------------
// 点群PNGタイルから原点とデータを生成するためのクラス
import { PcpngOptimizer } from "./modules/PcpngOptimizer/PcpngOptimizer";
//------------------------------------------------------------------------------
// 追加プロトコル（地形用と点群用）
import gsidemToTerrainRgb from "./modules/protocols/gsidemToTerrainRgb";
import pcpngTileEmptyCanvas from "./modules/protocols/pcpngTileEmptyCanvas";
//------------------------------------------------------------------------------
// css
import "./css/style.css";

//******************************************************************************
// プロトコルの追加（標高と点群はこのプロトコルをURLの前につけておく）
//------------------------------------------------------------------------------
// 標高用
MapLibreGL.addProtocol('gsidem', gsidemToTerrainRgb);
//------------------------------------------------------------------------------
// 点群用
MapLibreGL.addProtocol('pcpng', pcpngTileEmptyCanvas);

//******************************************************************************
// 各種変数を格納するためのアプリケーション変数
const app = {
	//--------------------------------------------------------------------------
	// maplibre用
	maplibre: {
		// MapboxOverlayインスタンスを格納する変数
		controls: {}
	},
	//--------------------------------------------------------------------------
	// 点群PNG解析等処理用インスタンス
	pcpngOptimizer: new PcpngOptimizer(options.pcpng.tiles[0].replace("pcpng://", "")),
	//--------------------------------------------------------------------------
	// 各種計測関連用
	// sphereのドラッグを管理するためのオブジェクト
	drag: {},
	// 計測用に配置する線分と球を管理するためのクラスを初期化
	measures: {
		segments: new MeasureSegments({
			color: [255, 0, 0],
			width: 3,
			unit: "pixels"
		}),
		// mapオブジェクトを渡したいので後で生成
		spheres: new MeasureSpheres({
			colors: [[255, 0, 0], [255, 255, 0]],
			radius: 5
		}),
	},
	//--------------------------------------------------------------------------
	// 各種アクション用
	action: {
		// ポイント選択モード
		mode: null,
		// ドラッグ中の球を格納
		drag: {},
		// グラデーション表示
		gradation: false,
		// 点群ハイライト用
		onmouse: new OnMousePoint({ radius: 5 }),
		// 球クリックアクション
		trigger: new TriggerMeasure(document.getElementById("wrapper"))
	},
	//--------------------------------------------------------------------------
	// 登録するPointCloudLayerインスタンスを参照としてここに格納
	pcpngLayers: {},
	// 点群PNGファイル処理中のキーを格納する変数
	loading: {},
	// 破棄待ち中のキーを格納する変数
	disposePending: new Set()
};

//**************************************************************************
// 背景地図プルダウンの登録
style.basemaps.forEach((value) => {
	const option = document.createElement("option");
	option.value = value.key;
	option.textContent = value.title;
	option.selected = value.initial;
	document.querySelector(`select[name="basemap"]`).appendChild(option);
});

//**************************************************************************
// maplibreを初期化
app.maplibre.map = new MapLibreGL.Map({
	container: "app",
	...options.map,
	// refreshExpiredTiles: false,
	style: {
		version: 8,
		// データソース
		sources: {
			// 背景地図のソースは後で登録
			// 点群PNGタイル
			pcpng_tile: options.pcpng,
			// 標高PNGタイル
			gsj_dem: style.dem
		},
		// 表示レイヤ
		layers: [
			// 背景地図のレイヤは後で登録
			// 点群PNGタイル
			// タイル表示のためではなく、タイル取得イベントを受け取るために使用
			// 実際はraster-opacity=0で使用（表示不要ため）
			// layout.visibility="none"でも非表示にできるがイベントを発生させられない
			{
				id: "pointcloud",
				type: "raster",
				source: "pcpng_tile",
				paint: {
					"raster-opacity": 0,
				},
			}
		],
		// 地形表現指定
		terrain: {
			source: "gsj_dem",
			exaggeration: 1
		}
	}
});

//**************************************************************************
// コントロールを追加
app.maplibre.map.addControl(new MapLibreGL.NavigationControl());
app.maplibre.map.addControl(new MapLibreGL.ScaleControl());

//**************************************************************************
// 点群PNGタイルを実際に表示するためのオーバーレイをコントロールとして登録
app.maplibre.controls.deckOverlay = new MapboxOverlay({
	interleaved: false,
	layers: [
		// デフォルトでは空
	]
});
app.maplibre.map.addControl(app.maplibre.controls.deckOverlay);

//**************************************************************************
/**
 * 背景地図の初期登録・更新で使用する関数を用意
 * @param {string} sourceId basemap id to show
*/
const updateBasemap = (sourceId) => {
	//----------------------------------------------------------------------
	// レイヤ識別子は固定
	const layerId = "background";
	// 該当レイヤはあれば破棄
	const layer = app.maplibre.map.getLayer(layerId);
	if (typeof layer !== "undefined") {
		app.maplibre.map.removeLayer(layerId);
	}
	//----------------------------------------------------------------------
	// ソース識別子を指定して点群レイヤの前に登録（後ろでも差し支えない）
	app.maplibre.map.addLayer({
		id: "background",
		type: "raster",
		source: sourceId,
		paint: {
			"raster-opacity": 1,
		},
	}, "pointcloud");
};

//**************************************************************************
/**
 * タイル座標から点群PNGタイルを取得してPointCloudLayerを作る関数
 * @param {{x, y, z, key}} coords tile coordinates: x, y, z, key
 * @returns	{Promise<LightPointCloudLayer|false>} false or instance of LightPointCloudLayer
*/
const createPointCloudLayer = async (coords) => {
	//----------------------------------------------------------------------
	// 処理成否で分岐
	let layer;
	// ここでエラー対策しても手遅れだからその前にやるべき
	// for (let i = 0; i < 3; i += 1) {
	//----------------------------------------------------------------------
	// キーとタイルURLテンプレート
	const key = coords.key;
	//----------------------------------------------------------------------
	// 点群PNGタイルを取得して原点、差分距離、点数を求める
	const pcpngData = await app.pcpngOptimizer.optimize(coords, options.pcpng.mimeType);
	if (typeof key === "undefined" || pcpngData === false) {
		console.log("optimize failed:", key);
		// await new Promise(resolve => setTimeout(resolve, 1000));
		// 処理失敗または処理済み
		// return false;
	} else {
		// 処理成功
		// deck.gl用のコンテキストを取得（頂点バッファ登録用）
		const device = app.maplibre.controls.deckOverlay._deck.device;
		// PointCloudLayerのdata値を構築
		// https://deck.gl/docs/developer-guide/performance
		const data = {
			length: pcpngData.count,
			attributes: {}
		};
		Object.keys(pcpngData.data).forEach((value) => {
			const size = pcpngData.data[value].length / pcpngData.count;
			data.attributes[`get${value.substring(1)}`] = {
				buffer: device.createBuffer(pcpngData.data[value]),
				size,
				offset: 0,
				stride: size * pcpngData.data[value].BYTES_PER_ELEMENT
			};
		});
		// 拡張点群レイヤインスタンスを生成する
		layer = new CustomPointCloudLayer({
			id: key,
			data,
			// fp64はcoordinateSystemがLNGLAT以外のときは無効でOK
			// 一度作ったレイヤのバッファ差し替えがないのでupdateTriggersも不要
			fp64: false,
			getNormal: [0, 0, 1],
			sizeUnit: "pixels",
			// pointSize: 1.9,
			pointSize: 5,
			pickable: true,
			coordinateSystem: COORDINATE_SYSTEM.METER_OFFSETS,
			coordinateOrigin: [
				pcpngData.origin.lng,
				pcpngData.origin.lat,
				0
			],
			material: false,
			autoHighlight: true,
			highlightColor: [255, 255, 0, 255],
			pcpngData
		});
		// }
		if (typeof layer === "undefined") {
			return false;
		} else {
			return layer.setCustomUniforms(
				options.gradation
			).useGradation(
				app.action.gradation
			);
		}
	};
}

//******************************************************************************
// 描画レイヤ更新処理関数
//------------------------------------------------------------------------------
// 描画レイヤ更新関数
const respreadPointCloud = Lodash.debounce(() => {
	const bounds = app.maplibre.map.getBounds();
	const visibleIds = app.maplibre.map.style.sourceCaches.pcpng_tile
		.getRenderableIds()
		.filter(tileID => keyInBounds(tileID, bounds, 1.5))
		.filter(tileID => typeof tileID !== "undefined");
	for (const key of visibleIds) {
		if (typeof app.pcpngLayers[key] === "undefined" && !app.loading[key]) {
			app.loading[key] = true;

			const coords = keyToTileCoords(key);
			createPointCloudLayer(coords).then(layer => {
				if (layer) {
					app.pcpngLayers[key] = layer;
				}
				delete app.loading[key];
				respreadPointCloud(); // 再描画を予約
			});
		}
	}

	// --- ここを追加 ---
	if (app.disposePending.size > 0) {
		app.disposePending.forEach((key) => {
			if (
				typeof app.pcpngLayers[key] !== "undefined"
				&& app.pcpngLayers[key] instanceof CustomPointCloudLayer
			) {
				// app.pcpngLayers[key].props.data = null;
				delete app.pcpngLayers[key];
			}
		});
		app.disposePending.clear();
	}

	//--------------------------------------------------------------------------
	// データ取得に成功したレイヤのみ抽出
	const layers = Object.values(app.pcpngLayers).filter((value) => {
		return value !== false;
	});
	// バッファは転送済みなのでsetPropsの呼び出し回数が多くても問題ないかも
	app.maplibre.controls.deckOverlay.setProps({
		layers: [
			...layers,
			app.action.onmouse.getLayer(),
			app.measures.segments.getLayer(),
			app.measures.spheres.getLayer(),
		]
	});
	//--------------------------------------------------------------------------
	// 確認用：表示に使用しているタイル数と点数を出力
	const count = layers.reduce((acc, cur) => {
		return acc + (
			cur instanceof CustomPointCloudLayer ? cur.props.data.length : 0
		);
	}, 0);
	console.log(`[load] Total Pcpng Tiles: ${layers.length}, Total points ${count}`);
}, 50);


const tileToSpreadOrNot = (tileID, params) => {
	const coordinates = keyToTileCoords(tileID);
	if (coordinates.z <= Math.floor(app.maplibre.map.getZoom())) return true;
	/* tile center → lnglat */
	const n = Math.pow(2, coordinates.z);
	const lng = (coordinates.x + 0.5) / n * 360 - 180;
	const lat = Math.atan(
		Math.sinh(Math.PI * (1 - 2 * (coordinates.y + 0.5) / n))
	) * 180 / Math.PI;
	if (!app.maplibre.map.getBounds().contains({ lng, lat })) return false;
	/* tile center → screen pixel */
	const tilePx = app.maplibre.map.project({ lng, lat });
	const dx = tilePx.x - params.centerPixel.x;
	const dy = tilePx.y - params.centerPixel.y;
	/* 距離判定 */
	return (dx * dx + dy * dy) < params.distance;
};

const getParamsForTilesSpread = (factor = 0.1) => {
	/* canvas (Retina対応: CSS pixel使用) */
	const canvas = app.maplibre.map.getCanvas();
	const width = canvas.clientWidth;
	const height = canvas.clientHeight;
	/* 画面中心 */
	const center = app.maplibre.map.getCenter();
	const centerPixel = app.maplibre.map.project(center);
	/* 画面距離閾値 */
	const MAX_PIXEL_DISTANCE = Math.sqrt(
		width ** 2 + height ** 2
	) * factor;
	const distance = MAX_PIXEL_DISTANCE ** 2;
	return { centerPixel, distance };
};


//******************************************************************************
// 点群レイヤを走査して破棄判定・破棄実行する関数
const disposePointClouds = () => {

	const params = getParamsForTilesSpread();

	// const ids	= app.maplibre.map.style.sourceCaches.pcpng_tile.getRenderableIds();
	// bounds範囲内だけを取り出す
	const bounds = app.maplibre.map.getBounds();
	const ids = app.maplibre.map.style.sourceCaches.pcpng_tile.getRenderableIds().filter((tileID) => {
		// return keyInBounds(tileID, bounds, 1.5);
		if (typeof tileID === "undefined") return false;
		if (!keyInBounds(tileID, bounds, 1.5)) return false;
		// const t = keyToTileCoords(tileID);
		// /* 親タイルは残す */
		// if (t.z < Math.floor(app.maplibre.map.getZoom())) return true;
		// return tileToSpreadOrNot(tileID);
		return tileToSpreadOrNot(tileID, params);
	});

	const createPromises = [];
	ids.forEach((key) => {
		if (typeof app.pcpngLayers[key] === "undefined") {
			if (app.loading[key]) return;
			app.loading[key] = true;
			const coords = keyToTileCoords(key);
			const promise = createPointCloudLayer(coords).then((layer) => {
				if (layer) {
					app.pcpngLayers[key] = layer;
				}
				delete app.loading[key];
			});
			createPromises.push(promise);
		}
	});

	console.log("=== 空白調査 ===");
	console.log("visibleIds:", ids);
	const layerKeys = Object.keys(app.pcpngLayers);
	console.log("layerKeys:", layerKeys);
	// visible にあるのに layer に無いもの
	const missing = ids.filter(id => !layerKeys.includes(id));
	console.log("missingKeys:", missing);
	// もし missing があれば keyInBounds も確認
	if (missing.length > 0) {
		const bounds = app.maplibre.map.getBounds();
		missing.forEach(k => {
			console.log(
				"key:", k,
				"keyInBounds:", keyInBounds(k, bounds, 1.5)
			);
		});
	}
	// 可視だけど layer に存在しないキー
	const missingKeys = ids.filter(
		(key) => typeof app.pcpngLayers[key] === "undefined"
	);
	if (missingKeys.length > 0) {
		console.log("=== MISSING LAYERS ===");
		console.log("missingKeys:", missingKeys);
		// 1つだけ詳細確認
		const sample = missingKeys[0];
		console.log("sample key:", sample);
		console.log("layer object:", app.pcpngLayers[sample]);
	}
	// layer にはあるが data が壊れている可能性チェック
	const brokenKeys = ids.filter((key) => {
		const layer = app.pcpngLayers[key];
		if (!layer) return false;
		if (!layer.props) return true;
		if (!layer.props.data) return true;
		if (typeof layer.props.data.length === "undefined") return true;
		if (layer.props.data.length === 0) return true;
		return false;
	});
	if (brokenKeys.length > 0) {
		console.log("=== BROKEN LAYERS ===");
		console.log("brokenKeys:", brokenKeys);
		const sample = brokenKeys[0];
		console.log("sample key:", sample);
		console.log("layer.props:", app.pcpngLayers[sample]?.props);
	}

	// 破棄が実行されたか否かを確認するためのフラグを準備
	// let dispose = false;
	// 登録中の点群を走査して削除対象を探す
	Object.keys(app.pcpngLayers).forEach((key) => {
		// 描画対象に含まれない場合に削除対象とし実際に削除するか検査
		if (ids.indexOf(key) === -1) {
			// タイル座標を調べる
			const tile = keyToTileCoords(key);
			// // 親タイル、子タイル（各1世代）を格納する配列を用意
			// const required = [];
			// // 親タイルを取得して格納
			// const parents = findParents(tile);
			// required.push(parents);
			// // 子タイルを取得して格納
			// const children = findChildren(tile);
			// required.push(children);

			// 親タイルを取得（1世代想定）
			const parentTile = findParents(tile)[0];
			const parentKey = parentTile ? tileCoordsToKey(parentTile) : null;
			const required = [];
			// 親
			if (parentKey) required.push(parentKey);
			// 子
			const children = findChildren(tile);
			const childrenKeys = children.map(value => tileCoordsToKey(value));
			required.push(...childrenKeys);

			// // 親タイル、子タイルのいずれかが描画対象に含まれるか調べる
			// const anyRequired = required.flat().map((value) => {
			// 	return tileCoordsToKey(value);
			// }).reduce((acc, cur) => {
			// 	return ids.indexOf(cur) !== -1 || acc;
			// }, false);
			const anyRequired = required.reduce((acc, cur) => {
				return ids.indexOf(cur) !== -1 || acc;
			}, false);

			// 結果で分岐
			if (anyRequired === false) {
				// 親タイル子タイルがいずれも描画対象に含まれない場合は破棄予約・確定
				app.disposePending.add(key);
				// dispose = true;
			} else {
				// 親タイルまたは子タイルのいずれかが描画対象
				// // 親タイルが描画対象か否か
				// const parentRequired = ids.indexOf(parent) !== -1;
				// // 親タイルが登録済みか否か
				// const parentRegistered = app.pcpngLayers[parent] !== void (0);
				const parentRequired = parentKey ? ids.indexOf(parentKey) !== -1 : false;
				const parentRegistered = parentKey ? app.pcpngLayers[parentKey] !== void (0) : false;

				// 結果で分岐
				if (parentRequired && parentRegistered) {
					// 親タイルが描画対象でかつ親タイルが登録済みの場合は破棄予約・確定
					// disposePointCloud(key);
					app.disposePending.add(key);
					// dispose = true;
				} else if (parentRequired === false) {
					// // 親タイルが描画対象でない場合は子タイルが描画対象に必ず含まれる
					// // 描画対象の子タイルを抽出
					// const childrenRequired = children.filter((value) => {
					// 	return ids.indexOf(value) !== -1
					// });
					// const childrenRegistered = childrenRequired.reduce((acc, cur) => {
					// 	return app.pcpngLayers[cur] !== void (0) && acc;
					// }, true);
					const childrenRequired = childrenKeys.filter(value => {
						return ids.indexOf(value) !== -1;
					});
					const childrenRegistered = childrenRequired.reduce((acc, cur) => {
						return typeof app.pcpngLayers[cur] !== "undefined" && acc;
					}, true);

					// 結果で分岐
					if (childrenRegistered === true) {
						// 描画対象子タイルがすべて登録済みの場合は破棄予約・確定
						// disposePointCloud(key);
						app.disposePending.add(key);
						// dispose = true;
					}
				}
			}
		}
	});

	Promise.all(createPromises).then(() => {
		requestAnimationFrame(() => {
			// const finalLayers = [
			// 	...Object.values(app.pcpngLayers).filter(v => v !== false),
			// 	app.action.onmouse.getLayer(),
			// 	app.measures.segments.getLayer(),
			// 	app.measures.spheres.getLayer(),
			// ];
			app.maplibre.controls.deckOverlay.setProps({
				layers: [
					...Object.values(app.pcpngLayers).filter(v => v !== false),
					app.action.onmouse.getLayer(),
					app.measures.segments.getLayer(),
					app.measures.spheres.getLayer(),
				]
			});
		});
	});

	// if (dispose === true) {
	// requestAnimationFrame(() => {
	// const finalLayers = [
	// 	...Object.values(app.pcpngLayers).filter(value => value !== false),
	// 	app.action.onmouse.getLayer(),
	// 	app.measures.segments.getLayer(),
	// 	app.measures.spheres.getLayer(),
	// ];
	// console.log("=== DECK SETPROPS ===");
	// console.log("layer count:", finalLayers.length);
	// console.log("layer ids:", finalLayers.map(l => l.id));

	// app.maplibre.controls.deckOverlay.setProps({
	// 	layers: [
	// 		...Object.values(app.pcpngLayers).filter(value => value !== false),
	// 		app.action.onmouse.getLayer(),
	// 		app.measures.segments.getLayer(),
	// 		app.measures.spheres.getLayer(),
	// 	]
	// });

	// // GC最適化処理
	// const device = app.maplibre.controls.deckOverlay.deck?.layerManager?.context?.device;
	// if (typeof device !== "undefined" && device !== null) {
	// 	device.submit();
	// 	device.resourceManager?.releaseAll();
	// 	device.resourceManager?.prune();
	// }
	// });
	// }
	const layers = Object.values(app.pcpngLayers).filter(value => value !== false);
	const count = layers.reduce((acc, cur) => {
		return acc + (
			cur instanceof CustomPointCloudLayer ? cur.props.data.length : 0
		);
	}, 0);
	console.log(`[idle] Total Pcpng Tiles: ${layers.length}, Total points ${count}`);
};
//------------------------------------------------------------------------------
/**
 * 破棄実行関数
 */
const cleanup = () => {
	//--------------------------------------------------------------------------
	// 処理中タイルがある場合は再実行を予約
	if (Object.keys(app.loading).length > 0) {
		setTimeout(() => {
			cleanup();
		}, 50);
	} else {
		// 処理中タイルがなくなったら時間差で破棄実行
		setTimeout(disposePointClouds, 100);
	}
};

//******************************************************************************
// MapboxOverlayイベント
// MapboxOverlayインスタンス生成時でも良いはずだが可読性のためsetPropsで記述
app.maplibre.controls.deckOverlay.setProps({
	//--------------------------------------------------------------------------
	// クリック処理
	onClick: async function (picking, event) {
		// 測定モード別ポイント選択数の上限
		const maxCounts = {
			point: 1,
			waypoints: Infinity,
			triangle: 3
		};
		// 測定モードが選択されていない場合は処理しない
		if (Object.keys(maxCounts).includes(app.action.mode) === false) {
			return;
		}
		// クリック位置に事物があった場合のみ処理
		// ただし事物がどのレイヤに属しているかで分岐が必要
		if (picking.picked === true) {
			// クリック対象で分岐
			switch (picking.layer.constructor.name) {
				// 点群の場合
				case CustomPointCloudLayer.name:
					// 選択済みポイント数が上限値以上の場合は一旦球を破棄する
					if (app.measures.spheres.getCount() >= maxCounts[app.action.mode]) {
						app.measures.segments.clear();
						app.measures.spheres.clear();
					}
					{
						// pickingした頂点から頂点の座標と色を調べる
						const vertex = getPickedVertex(picking);
						if (vertex !== null) {
							// ポイントを追加する
							app.measures.segments.update(vertex);
							app.measures.spheres.update(vertex);
							// triangleで3つのポイントが選択された場合のみの特別処理
							if (
								app.action.mode === "triangle"
								&& app.measures.spheres.getCount() === maxCounts.triangle
							) {
								// 最初のポイントと最後のポイントを結合する
								const vertex = app.measures.spheres.getFirst();
								app.measures.segments.update(vertex);
							}
							// レイヤーを更新する
							app.maplibre.controls.deckOverlay.setProps({
								layers: [
									...Object.values(app.pcpngLayers).filter(value => value !== false),
									app.action.onmouse.getLayer(),
									app.measures.segments.getLayer(),
									app.measures.spheres.getLayer(),
								]
							});
						}
					}
					app.action.trigger.dispose();
					break;
				// 球の場合
				case app.measures.spheres.getName():
					// pickingした頂点から頂点の座標と色を調べる
					app.action.trigger.clickSphere(
						picking, app.action.mode
					).update(app.maplibre.map);
					break;
				// 線分の場合
				case app.measures.segments.getName():
					app.action.trigger.clickSegments(
						picking, event
					).update(app.maplibre.map);
					break;
				// 上記以外（呼ばれない）
				default:
				//
			}
		}
	},
	//--------------------------------------------------------------------------
	// マウスオーバー処理
	onHover: Lodash.throttle((picking) => {
		// pickingの状態で分岐
		if (picking.picked === false) {
			// picking対象でない場合
			// カーソルの変更
			app.maplibre.map.getCanvas().style.cursor = "inherit";
			// 地図ドラッグ操作を有効化
			app.maplibre.map.dragPan.enable();
			app.maplibre.map.dragRotate.enable();
			// オンマウスのポイントを輝度をあげて大きくする
			// picking対象がないのでハイライト球を破棄する
			if (app.action.onmouse.getCount() > 0) {
				app.maplibre.controls.deckOverlay.setProps({
					layers: [
						...Object.values(app.pcpngLayers).filter(value => value !== false),
						app.action.onmouse.highlight(null).getLayer(),
						app.measures.segments.getLayer(),
						app.measures.spheres.getLayer(),
					]
				});
			}
		} else {
			// picking対象の場合はその対象の種類で分岐
			if (picking.layer.constructor.name === app.measures.spheres.getName()) {
				// 球の場合は地図ドラッグ操作を無効化
				app.maplibre.map.dragPan.disable();
				app.maplibre.map.dragRotate.disable();
			} else {
				// 球でない場合は地図ドラッグ操作を有効化
				app.maplibre.map.dragPan.enable();
				app.maplibre.map.dragRotate.enable();
			}
			// picking対象の種類で分岐
			switch (picking.layer.constructor.name) {
				// 点群の場合
				case CustomPointCloudLayer.name:
					// カーソル形状を点群専用に変更
					app.maplibre.map.getCanvas().style.cursor = "default";
					// 球選択中か否かで分岐
					if (typeof app.action.drag.sphere !== "undefined") {
						// 球選択中の場合
						// ポップアップがあれば破棄する
						app.action.trigger.dispose();
						// pickingしたポイントの座標と色を調べる
						const vertex = getPickedVertex(picking);
						if (typeof vertex !== null) {
							// triangleかつsphere数3かつ最初のsphereを動かす場合
							if (
								app.action.mode === "triangle"
								&& app.measures.spheres.getCount() === 3
								&& app.action.drag.sphere.index === 0
							) {
								// segmentsの最後の値も更新する
								app.measures.segments.update(vertex, 3);
							}
							// レイヤを更新する
							app.maplibre.controls.deckOverlay.setProps({
								layers: [
									...Object.values(app.pcpngLayers).filter(value => value !== false),
									app.action.onmouse.getLayer(),
									app.measures.segments.update(vertex, app.action.drag.sphere.index).getLayer(),
									app.measures.spheres.update(vertex, app.action.drag.sphere.index).getLayer(),
								]
							});
						}
					} else {
						// 球選択中でない場合
						// レイヤを更新する
						app.maplibre.controls.deckOverlay.setProps({
							layers: [
								...Object.values(app.pcpngLayers).filter(value => value !== false),
								app.action.onmouse.highlight(picking).getLayer(),
								app.measures.segments.getLayer(),
								app.measures.spheres.getLayer(),
							]
						});
					}
					break;
				// 球の場合
				case app.measures.spheres.getName():
					// カーソル形状を球専用に変更
					app.maplibre.map.getCanvas().style.cursor = "pointer";
					// ハイライト球を非表示にする
					if (app.action.onmouse.getCount() > 0) {
						app.maplibre.controls.deckOverlay.setProps({
							layers: [
								...Object.values(app.pcpngLayers).filter(value => value !== false),
								app.action.onmouse.highlight(null).getLayer(),
								app.measures.segments.getLayer(),
								app.measures.spheres.getLayer(),
							]
						});
					}
					break;
				// 線分の場合
				case app.measures.segments.getName():
					// カーソル形状を線分専用に変更
					app.maplibre.map.getCanvas().style.cursor = "crosshair";
					// ハイライト球を非表示にする
					if (app.action.onmouse.getCount() > 0) {
						app.maplibre.controls.deckOverlay.setProps({
							layers: [
								...Object.values(app.pcpngLayers).filter(value => value !== false),
								app.action.onmouse.highlight(null).getLayer(),
								app.measures.segments.getLayer(),
								app.measures.spheres.getLayer(),
							]
						});
					}
					break;
				// 上記以外（呼ばれない）
				default:
				//
			}
		}
	}, 80),
});

//******************************************************************************
// maplibreイベント
//------------------------------------------------------------------------------
// maplibreの準備が整ってからソースとレイヤを追加
app.maplibre.map.on("load", () => {
	//--------------------------------------------------------------------------
	// 背景地図のソースを登録（raster前提）
	style.basemaps.forEach((value) => {
		app.maplibre.map.addSource(value.key, value);
	});
	//--------------------------------------------------------------------------
	// 初期表示地図のソースを決めて地図レイヤを登録
	const sourceId = style.basemaps.find((value) => {
		return value.initial;
	}).key;
	updateBasemap(sourceId);
});
//------------------------------------------------------------------------------
// mousedown位置に球があればオブジェクトに格納する
app.maplibre.map.on("mousedown", (event) => {
	//--------------------------------------------------------------------------
	// maplibreのクリック座標からdeckのオブジェクトを探す
	const pickedObject = app.maplibre.controls.deckOverlay.pickObject({
		x: event.point.x,
		y: event.point.y
	});
	//----------------------------------------------------------------------
	// オブジェクトが見つかり、かつそれが球の場合に変数に格納しておく
	if (
		pickedObject !== null
		&& pickedObject.layer.constructor.name === app.measures.spheres.getName()
	) {
		app.action.drag.sphere = pickedObject;
	}
});
//------------------------------------------------------------------------------
// mouseup時に球があれば破棄する
app.maplibre.map.on("mouseup", (event) => {
	//--------------------------------------------------------------------------
	// マウスアップで変数に格納したsphereを破棄する
	delete app.action.drag.sphere;
});
//--------------------------------------------------------------------------
// ズーム中、球の上にカーソルがあるとハイライト球の処理ができないのでハイライト球を破棄する
// zoomイベント時はmoveイベントも発生するためmoveイベントのみで可
// app.maplibre.map.on( "zoom", ( event ) => {
// 	if ( app.action.onmouse.getCount() > 0 ) {
// 		app.maplibre.controls.deckOverlay.setProps({
// 			layers: [
// 				...Object.values( app.pcpngLayers ).filter( value => value !== false ),
// 				app.action.onmouse.highlight( null ).getLayer(),
// 				app.measures.segments.getLayer(),
// 				app.measures.spheres.getLayer(),
// 			]
// 		});
// 	}
// });
//------------------------------------------------------------------------------
// ドラッグ中はハイライト球を破棄する
app.maplibre.map.on("move", Lodash.throttle((event) => {
	if (app.action.onmouse.getCount() > 0) {
		app.maplibre.controls.deckOverlay.setProps({
			layers: [
				...Object.values(app.pcpngLayers).filter(value => value !== false),
				app.action.onmouse.highlight(null).getLayer(),
				app.measures.segments.getLayer(),
				app.measures.spheres.getLayer(),
			]
		});
	}
}, 80));
//------------------------------------------------------------------------------
// sourcedataイベント発生によりPointCloudLayerを作成して登録
app.maplibre.map.on("sourcedata", async (event) => {
	//--------------------------------------------------------------------------
	// この中でclearTiles()/update()すると無限ループになる
	//--------------------------------------------------------------------------
	// イベント判定
	if (event.sourceId === "pcpng_tile" && event.tile && event.tile.state === "loaded") {
		// キーを取得
		const key = event.tile.tileID.canonical.key;
		if (typeof key === "undefined") return;
		console.log("create attempt:", key);

		// キー未登録の場合はレイヤを構築して登録
		// if ( app.pcpngLayers[ key ] === void( 0 )) {
		// boundsを取得
		const bounds = app.maplibre.map.getBounds();
		// キー未登録かつ範囲内の場合はレイヤを構築して登録
		if (
			typeof app.pcpngLayers[key] === "undefined"
			&& keyInBounds(key, bounds, 1.5)
		) {
			// 処理中フラグを立てる
			app.loading[key] = 1;
			// 点群レイヤを構築する
			app.pcpngLayers[key] = await createPointCloudLayer(
				event.tile.tileID.canonical
			);
			// 処理中フラグを解除する
			delete app.loading[key];
			// タイル登録時に、自タイルに関連する先祖、子孫のタイル破棄に関する処理（idle時と別）
			// 子タイルが不要になるのでここで削除（複数階層下まで見る）
			const children = findChildren(event.tile.tileID.canonical, 4);
			children.forEach((value) => {
				const key = tileCoordsToKey(value);
				// disposePointCloud(key);
				app.disposePending.add(key);
			});
			// 親タイルを調べる（複数階層上まで見る）
			const parents = findParents(event.tile.tileID.canonical, 4);
			parents.forEach((parent) => {
				const siblings = findChildren(parent);
				const flag = siblings.reduce((acc, cur) => {
					const key = tileCoordsToKey(cur);
					return acc && typeof app.pcpngLayers[key] !== "undefined";
				}, true);
				if (flag === true) {
					const key = tileCoordsToKey(parent);
					// disposePointCloud(key);
					app.disposePending.add(key);
				}
			});
			// 描画処理（最小ズームレベルを低めに設定することで破棄イベントも取得できる）
			// thinRespreadPointCloud();
			respreadPointCloud();
			// app.respreadDebounced();
		}
	}
});
//------------------------------------------------------------------------------
// sourcedataイベントを再発生させるためタイルキャッシュ等破棄を実行
app.maplibre.map.on('moveend', (event) => {
	//--------------------------------------------------------------------------
	// キャッシュ破棄
	app.maplibre.map.style.sourceCaches.pcpng_tile.clearTiles();
	app.maplibre.map.style.sourceCaches.pcpng_tile.update(app.maplibre.map.transform);
	// 再読み込み：pcpng_tileのみリロードできているはず
	// app.maplibre.map.triggerRepaint();
	// app.maplibre.map.style.sourceCaches.pcpng_tile.reload();
	// respreadPointCloud();
});
//------------------------------------------------------------------------------
// idleイベントは必ずmoveendイベントの後
app.maplibre.map.on('idle', (event) => {
	//--------------------------------------------------------------------------
	// 点群レイヤ破棄
	cleanup();
});
//------------------------------------------------------------------------------
// 地図移動完了時に球のサイズを調整する
app.maplibre.map.on('moveend', (event) => {
	const zoom = app.maplibre.map.getZoom();
	const size = (14 / zoom) ** 7;
	app.measures.spheres.scaleUpdate(size);
	app.action.onmouse.scaleUpdate(size);
});
//------------------------------------------------------------------------------
// 地図移動時にポップアップがあればその位置を更新する
app.maplibre.map.on('move', (event) => {
	app.action.trigger.update(app.maplibre.map);
});

//******************************************************************************
// 操作パネル関連
//------------------------------------------------------------------------------
// 操作パネルを閉じる
document.querySelector('#nav div.header span').addEventListener('click', (event) => {
	document.querySelector('#nav').classList.add("hide");
});
//------------------------------------------------------------------------------
// 操作パネルを開く
document.querySelector('#open').addEventListener('click', (event) => {
	document.querySelector('#nav').classList.remove("hide");
});
//------------------------------------------------------------------------------
// 選択モード変更
document.querySelectorAll("#nav dd.measurement span.btn").forEach((element) => {
	element.addEventListener("click", (event) => {
		// すべてのボタンのonクラスを取り除いて、クリックされた要素に対してだけonクラスを付与
		element.parentNode.querySelectorAll("span.btn").forEach((element) => {
			element.classList.remove("on");
		});
		element.classList.add("on");
		// 処理モードを変数に格納
		app.action.mode = element.dataset.mode;
		// クリック時には球と線分をクリアしてレイヤをセット
		app.maplibre.controls.deckOverlay.setProps({
			layers: [
				// false以外を展開
				...Object.values(app.pcpngLayers).filter(layer => layer !== false),
				app.action.onmouse.getLayer(),
				// 球と線分はクリア
				app.measures.segments.clear().getLayer(),
				app.measures.spheres.clear().getLayer(),
			]
		});
		// 吹き出しをクリア
		app.action.trigger.dispose();
	});
});
//------------------------------------------------------------------------------
// 点群色調変更
document.querySelectorAll("#nav dd.color span.btn").forEach((element) => {
	element.addEventListener("click", (event) => {
		// 変数にグラデーションモードか否かをセット
		app.action.gradation = event.target.dataset.mode === "gradation";
		// レイヤをセット
		app.maplibre.controls.deckOverlay.setProps({
			layers: [
				// false以外を展開してグラデーションモードをセット
				...Object.values(app.pcpngLayers).filter((layer) => {
					return layer !== false;
				}).map((layer) => {
					return layer.useGradation(app.action.gradation);
				}),
				app.action.onmouse.getLayer(),
				app.measures.segments.getLayer(),
				app.measures.spheres.getLayer(),
			]
		});
	});
});
//------------------------------------------------------------------------------
// カメラ位置変更
document.querySelectorAll("#nav dd.camera span.btn").forEach((element) => {
	element.addEventListener("click", (event) => {
		// bearing値を取得して適用
		const bearing = Number(event.target.dataset.bearing);
		app.maplibre.map.setBearing(bearing);
		// pitch値を取得（取得できない場合は現在のpitch値）して適用
		const pitch = Number(event.target.dataset.pitch ?? app.maplibre.map.getPitch());
		app.maplibre.map.setPitch(pitch);
	});
});
//------------------------------------------------------------------------------
// 背景地図の変更
document.querySelector(`#nav dd.basemap select[name="basemap"]`).addEventListener("change", (event) => {
	updateBasemap(event.target.value);
});








// マウスホイールのズーム感度を下げ高速ズーム時に標高タイル取得前にカメラが地下へ潜る可能性を低減（完全ではない）
// app.maplibre.map.scrollZoom.setZoomRate(1 / 600);
// app.maplibre.map.scrollZoom.setWheelZoomRate(1 / 1800);
const zoomRate = app.maplibre.map.scrollZoom._defaultZoomRate;
const wheelZoomRate = app.maplibre.map.scrollZoom._wheelZoomRate;
let zoomFlag = false;
let zoomStart = app.maplibre.map.getZoom();
app.maplibre.map.on("zoomstart", () => {
	zoomStart = app.maplibre.map.getZoom();
	setTimeout(() => {
		const z = app.maplibre.map.getZoom();
		const dz = z - zoomStart;
		if (dz > 0 && zoomFlag === false) {
			app.maplibre.map.scrollZoom.setZoomRate(zoomRate / 3);
			app.maplibre.map.scrollZoom.setWheelZoomRate(wheelZoomRate / 3);
		} else {
			app.maplibre.map.scrollZoom.setZoomRate(zoomRate / 2);
			app.maplibre.map.scrollZoom.setWheelZoomRate(wheelZoomRate / 2);
		}
		zoomFlag = true;
	});
});
// app.maplibre.map.on("zoomend", () => {
// 	const z = app.maplibre.map.getZoom();
// 	const dz = z - zoomStart;
// 	if (dz > 0) {
// 		if (Math.abs(dz) > 0.5) {
// 			app.maplibre.map.setZoom(
// 				zoomStart + Math.sign(dz) * 0.5
// 			);
// 		}
// 	}
// 	zoomFlag = false;
// });
